<template>
  <div class="partner-tab-sharing-level-cell">
    <BittsPopover
      v-if="variant && hasPopover"
      trigger="hover"
      placement="bottomLeft"
      :mount-to-body="true"
      :mouse-enter-delay="0.1"
      overlay-class="partner-tab-sharing-level-cell__popover"
      class="partner-tab-sharing-level-cell__trigger"
    >
      <PartnerTabSharingLevelPill :variant="variant" />
      <template #content>
        <PartnerTabSharingLevelPopover :populations="segmentPopulations" />
      </template>
    </BittsPopover>
    <PartnerTabSharingLevelPill v-else-if="variant" :variant="variant" />
    <span v-else class="text-sm text-neutral-text-placeholder">&ndash;</span>
  </div>
</template>

<script setup lang="ts">
import { BittsPopover } from '@crossbeam/bitts';

import { ICellRendererParams } from '@ag-grid-community/core';
import { computed } from 'vue';

import {
  GREENFIELD_SHARING,
  HIDDEN,
  OLAPS_ONLY,
  SHARING,
  type SharingType,
} from '@/constants/data_shares';

import type {
  PopulationSharingLevel,
  SharingLevelCellParamsType,
  SharingLevelVariantType,
  SharingPartnerRow,
  SharingSideType,
} from '../types';

import PartnerTabSharingLevelPill from './PartnerTabSharingLevelPill.vue';
import PartnerTabSharingLevelPopover from './PartnerTabSharingLevelPopover.vue';

const { params } = defineProps<{
  params: ICellRendererParams<SharingPartnerRow> & SharingLevelCellParamsType;
}>();

const LEVEL_VARIANT: Record<SharingType, SharingLevelVariantType> = {
  [GREENFIELD_SHARING]: 'all',
  [SHARING]: 'overlaps',
  [OLAPS_ONLY]: 'counts',
  [HIDDEN]: 'hidden',
};

const POPULATIONS_KEY: Record<
  SharingSideType,
  'incomingPopulations' | 'outgoingPopulations'
> = {
  incoming: 'incomingPopulations',
  outgoing: 'outgoingPopulations',
};

/** Request and Pending get their own hover behaviour in PGA-537. */
const VARIANTS_WITH_POPOVER = new Set<SharingLevelVariantType>([
  'all',
  'overlaps',
  'counts',
  'hidden',
]);

const variant = computed<SharingLevelVariantType | null>(() => {
  const level = params.data?.[params.side][params.segmentKey];
  return level ? LEVEL_VARIANT[level] : null;
});

const segmentPopulations = computed<PopulationSharingLevel[]>(() =>
  Object.values(params.data?.[POPULATIONS_KEY[params.side]] ?? {}).filter(
    (population) => population.segment === params.segmentKey,
  ),
);

const hasPopover = computed(
  () =>
    !!variant.value &&
    VARIANTS_WITH_POPOVER.has(variant.value) &&
    !!segmentPopulations.value.length,
);
</script>

<style lang="pcss">
@reference "../../../../css/theme.css";
.partner-tab-sharing-level-cell {
  @apply flex items-center justify-center h-full w-full py-6;
}
.partner-tab-sharing-level-cell__trigger {
  @apply h-full w-full;
}
.partner-tab-sharing-level-cell__popover.ant-popover {
  @apply w-[300px];

  .ant-popover-inner {
    @apply shadow-overlay;
  }
}
</style>
